import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { Entry, EntryCollection } from 'contentful'
import { RootState } from '~/types/store'

interface SchoolSubjectFields {
  title: string
  [key: string]: unknown
}

interface ProductFields {
  schoolSubject?: Entry<SchoolSubjectFields>
  [key: string]: unknown
}

// NOTE: the products handled here are Contentful product *entries* (accessed via
// `products.items` / `item.fields`), not propeller commerce products, so the
// shared `ProductArg` type does not apply to this module.
type ProductCollection = EntryCollection<ProductFields>

export interface ProductsState {
  products: ProductCollection | Record<string, never>
  productsPerPage: number
  schoolSubjects: Entry<SchoolSubjectFields>[]
  selectedSchoolSubjects: string[]
  selectedProductTypes: string[]
}

export function state (): ProductsState {
  return ({
  products: {},
  productsPerPage: 18,
  schoolSubjects: [],
  selectedSchoolSubjects: [],
  selectedProductTypes: []
})
}

export const mutations: MutationTree<ProductsState> = {
  SET_PRODUCTS (state, data: ProductCollection) {
    state.products = data
  },
  SET_SCHOOL_SUBJECTS (state, data: Entry<SchoolSubjectFields>[]) {
    state.schoolSubjects = data
  },
  SET_SELECTED_SCHOOL_SUBJECTS (state, subjects: string[]) {
    state.selectedSchoolSubjects = subjects
  },
  SET_SELECTED_PRODUCT_TYPES (state, data: string[]) {
    state.selectedProductTypes = data
  }
}

export const getters: GetterTree<ProductsState, RootState> = {
  totalPages (state) {
    if (Object.keys(state.products).length === 0) return 0
    return Math.ceil((state.products as ProductCollection).total / state.productsPerPage)
  },
  currentPage (state) {
    if (Object.keys(state.products).length === 0) return 0
    return Math.ceil((state.products as ProductCollection).skip / state.productsPerPage) + 1
  },
  schoolSubjectsFields (state) {
    if (!state.schoolSubjects.length) return []
    return state.schoolSubjects.map(subject => subject.fields)
  },
  schoolSubjectsOptions (state) {
    if (!state.selectedSchoolSubjects.length) return null
    return {
      'fields.schoolSubject.sys.contentType.sys.id': 'schoolSubject',
      'fields.schoolSubject.fields.title[in]': state.selectedSchoolSubjects.join(',')
    }
  },
  productTypeOptions (state) {
    if (!state.selectedProductTypes.length) return null
    return { 'fields.productType[in]': state.selectedProductTypes.join(',') }
  },
  productsWithBanners (state, getters, rootState) {
    const config = (rootState as RootState & { config: Entry<{ productsArchiveCTAs?: Entry<Record<string, unknown>>[] }> }).config
    const products = state.products as ProductCollection
    const banners = config.fields.productsArchiveCTAs
    if (!banners || banners.length === 0 || !products.items) return products.items

    const bannersWithCta = banners.map((banner) => {
      return {
        ...banner.fields,
        contentType: 'CallToAction'
      }
    })

    const productList: unknown[] = Array.from(products.items)
    const bannerPositions = [6, 16]

    bannersWithCta.forEach((banner, i) => {
      if (bannerPositions[i] > productList.length) {
        productList.push(banner)
      } else {
        productList.splice(bannerPositions[i], 0, banner)
      }
    })

    return productList
  }
}

interface GetProductsPayload {
  mutate?: boolean
  page?: number
}

export const actions: ActionTree<ProductsState, RootState> = {
  async get ({ state, commit, getters, rootGetters }, { mutate = true, page = 1 }: GetProductsPayload) {
    const skipNum = (page - 1) * state.productsPerPage
    const products = await this.$contentful.getEntries<ProductFields>({
      content_type: 'product',
      limit: state.productsPerPage,
      skip: skipNum,
      ...rootGetters['levels/levelOptions'],
      ...getters.schoolSubjectsOptions,
      ...getters.productTypeOptions
    })
    if (mutate) {
      const productsItems = products.items.map(item => item.fields)
      const getSubjectTitle = (subject?: Entry<SchoolSubjectFields>) => {
        if (!subject || !subject.fields) return ''
        return subject.fields.title
      }
      // manually sort products before commiting to avoid content flash
      const sortedProducts = Array.from(productsItems)
      sortedProducts.sort((a, b) => {
        return getSubjectTitle(a.schoolSubject).localeCompare(getSubjectTitle(b.schoolSubject))
      })
      products.items = sortedProducts as unknown as typeof products.items
      commit('SET_PRODUCTS', products)
    }
    return products
  },
  async getSchoolSubjects ({ commit }) {
    const { items } = await this.$contentful.getEntries<SchoolSubjectFields>({
      content_type: 'schoolSubject',
      order: 'fields.title'
    })
    commit('SET_SCHOOL_SUBJECTS', items)
  }
}
